import { useRef, useState, useEffect, useCallback } from 'react';

// ----------------------------------------------------------------------

interface UseInfiniteScrollOptions {
  /** Whether there are more pages to load. When false, the observer is torn down. */
  hasMore: boolean;
  /** True while a page request is in flight — prevents firing another load. */
  isLoading: boolean;
  /** Called when the sentinel scrolls into view and a load is allowed. */
  onLoadMore: () => void;
  /** Distance from the viewport at which to trigger, so the next page loads before the user hits the end. */
  rootMargin?: string;
}

/**
 * Sentinel-based infinite scroll (JT1-1528). Attach the returned ref to an element at the end of the
 * list; when it intersects the viewport and `hasMore && !isLoading`, `onLoadMore` fires once. There
 * was no infinite-scroll pattern in the codebase — all lists used offset pagination — so this is the
 * shared primitive the new list uses instead of TablePaginationCustom.
 *
 * Returns a CALLBACK ref: attach it with `ref={sentinelRef}`. Using node state (not a plain ref)
 * means the observer attaches correctly whenever the sentinel mounts/unmounts, and re-attaches when
 * `hasMore` flips. onLoadMore/isLoading are read through refs so a fresh callback each render doesn't
 * churn the observer.
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLDivElement>({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
}: UseInfiniteScrollOptions) {
  const [node, setNode] = useState<T | null>(null);
  const sentinelRef = useCallback((el: T | null) => setNode(el), []);

  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry?.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
      onLoadMoreRef.current();
    }
  }, []);

  useEffect(() => {
    // Nothing to observe, or no more pages — don't create an observer (tears down at the end).
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(handleIntersect, { rootMargin });
    observer.observe(node);

    return () => observer.disconnect();
  }, [node, hasMore, rootMargin, handleIntersect]);

  return sentinelRef;
}
